import cx from "classnames";

import { ReactComponent as CheckIcon } from "atoms/icons/check.svg";

type SwitchProps = {
  checked?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  checkedTone?: "success" | "primary";
  dataTestId?: string;
  name?: string;
  id?: string;
  ariaLabel?: string;
  onChange?: (value: boolean) => void;
};

export const Switch = ({
  checked,
  checkedTone = "success",
  disabled,
  readOnly,
  dataTestId,
  ariaLabel,
  onChange,
  name,
  id,
}: SwitchProps) => {
  if (name && id) {
    return (
      <SwitchV2
        checked={checked}
        checkedTone={checkedTone}
        dataTestId={dataTestId}
        disabled={disabled}
        id={id}
        name={name}
        ariaLabel={ariaLabel}
        readOnly={readOnly}
        onChange={onChange}
      />
    );
  }

  return (
    <label
      className={cx(
        "relative inline-flex cursor-pointer items-center",
        readOnly && "cursor-default",
        disabled && "cursor-not-allowed opacity-30",
      )}
      data-testid={dataTestId}
    >
      <input
        aria-label={ariaLabel}
        checked={checked}
        className="peer sr-only"
        disabled={readOnly || disabled}
        type="checkbox"
        onChange={(event) => {
          event.stopPropagation();
          onChange?.(event.target.checked);
        }}
      />
      <span
        className={cx(
          "peer block h-7 w-[52px] rounded-full peer-focus:outline-hidden peer-checked:after:translate-x-full",
          readOnly
            ? "bg-neutral-200/50"
            : cx(
                "bg-neutral-200",
                checkedTone === "primary"
                  ? "peer-checked:bg-primary-light"
                  : "peer-checked:bg-success/30",
              ),
        )}
      >
        <span
          className={cx(
            "peer absolute top-[3.5px] block h-[21px] w-[21px] rounded-full peer-checked:bg-success/30 peer-focus:outline-hidden",
            checked ? "right-[3.5px]" : "left-[3.5px]",
            readOnly && "bg-neutral-300/50",
            !readOnly &&
              (checked
                ? checkedTone === "primary"
                  ? "bg-primary"
                  : "bg-success"
                : "bg-neutral-400"),
          )}
        />
      </span>
      {checked && <CheckIcon className="absolute right-1.5 h-4 w-4 fill-white" />}
    </label>
  );
};

const SwitchV2 = ({
  checked,
  checkedTone = "success",
  disabled,
  readOnly,
  dataTestId,
  ariaLabel,
  onChange,
  name,
  id,
}: SwitchProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    onChange?.(event.target.checked);
  };

  // Not interactive and not focusable are the same thing here: `readonly` has no
  // effect on a checkbox, so "read only" has always been spelled `disabled`.
  const isInert = Boolean(disabled || readOnly);

  return (
    <div
      className={cx(
        "group/switch relative inline-flex h-7 w-[52px] shrink-0 items-center",
        disabled && "opacity-30",
      )}
      data-testid={dataTestId}
    >
      {/*
        The native checkbox IS the switch, rather than a hidden input sitting
        behind a `role="switch"` wrapper. Callers pass `id` so an external
        `<label htmlFor={id}>` can name the control — that label names the input,
        so with the role on the wrapper the thing assistive tech actually saw was
        an unnamed switch, and a form library's `aria-describedby` / `aria-invalid`
        had nowhere to land either. Owning the state natively also gets keyboard
        activation and focus for free instead of re-implementing them.

        Stretched transparently over the track so a pointer click lands on the
        control itself rather than being forwarded to it. `@tailwindcss/forms`
        gives every checkbox a white fill, a blue fill with a check image once
        checked, and a blue focus ring, so those are reset to keep it clear; the
        focus ring is drawn on the track below.

        No `role="switch"` on it: that would override the input's native checkbox
        role, and the announced role is not what was broken here — the missing name
        was. `checkbox` is what the non-`id` branch above has always exposed, so
        both branches now agree.
      */}
      <input
        aria-label={ariaLabel}
        checked={checked}
        className={cx(
          "peer absolute inset-0 z-10 m-0 h-full w-full appearance-none rounded-full border-0 bg-transparent bg-none focus:ring-0 focus:ring-offset-0 focus:outline-hidden",
          disabled ? "cursor-not-allowed" : readOnly ? "cursor-default" : "cursor-pointer",
        )}
        disabled={isInert}
        id={id}
        name={name}
        type="checkbox"
        onChange={handleChange}
        onClick={(event) => event.stopPropagation()}
      />

      <div
        className={cx(
          "pointer-events-none relative flex h-7 w-[52px] items-center rounded-full transition-all duration-200 group-hover/switch:opacity-75",
          // The focus ring lives here because the input itself is transparent.
          "peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2",
          readOnly
            ? "bg-neutral-200/50"
            : cx(
                "bg-neutral-200",
                checkedTone === "primary"
                  ? "peer-checked:bg-primary-light"
                  : "peer-checked:bg-success/30",
              ),
        )}
      >
        <div
          className={cx(
            "absolute top-1/2 left-[3px] flex h-[21px] w-[21px] -translate-y-1/2 items-center justify-center rounded-full transition-transform duration-200 ease-in-out",
            checked ? "translate-x-[25px]" : "translate-x-0 bg-neutral-400",
            checked && (checkedTone === "primary" ? "bg-primary" : "bg-success"),
            readOnly && "bg-neutral-300/50",
          )}
        >
          <div className="flex translate-z-0 items-center justify-center backface-hidden">
            <CheckIcon
              className={cx(
                "h-4 w-4 fill-white transition-opacity duration-200",
                checked ? "opacity-100" : "opacity-0",
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
